import { useState, useRef } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { UploadSimple, CheckCircle, Warning } from "@phosphor-icons/react";

// Upload a ClickFlare conversions export (Logs → Event Logs → Event Type =
// Conversion → Download) and match it to toolforme clicks by
// External ID == click_log.rid, marking matched clicks converted + payout.
// Preview (dry run) first; Import writes. Read-only until you click Import.
export default function AdminClickFlareImport() {
  const [file, setFile] = useState(null);
  const [excludeFacebook, setExcludeFacebook] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [wasDryRun, setWasDryRun] = useState(true);
  const fileRef = useRef(null);

  const run = async (dryRun) => {
    if (!file) { toast.error("Choose a ClickFlare CSV first"); return; }
    if (!dryRun) {
      const n = result?.matched_clicks;
      const ok = window.confirm(
        `Import will mark ${n ?? "the matched"} Autoinfo click(s) as converted `
        + `and write their payout. This changes production data. Continue?`,
      );
      if (!ok) return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("exclude_facebook", excludeFacebook ? "true" : "false");
      form.append("dry_run", dryRun ? "true" : "false");
      const { data } = await API.post("/admin/clickflare/import-conversions", form);
      setResult(data);
      setWasDryRun(dryRun);
      toast.success(dryRun
        ? `Preview: ${data.matched_clicks} matched of ${data.distinct_clicks_in_file}`
        : `Imported: ${data.updated} click(s) updated`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const Stat = ({ label, value, tone }) => (
    <div className="border border-slate-200 rounded-md p-3">
      <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-400">{label}</p>
      <p className={`text-xl font-mono font-bold mt-1 ${tone || "text-slate-900"}`}>{value}</p>
    </div>
  );

  return (
    <div className="p-6 max-w-4xl space-y-5">
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 font-bold">OPERATIONS / IMPORT</p>
        <h1 className="text-2xl font-bold text-slate-900">ClickFlare Import → Autoinfo</h1>
        <p className="text-sm text-slate-500 mt-1">
          Upload a ClickFlare conversions export. Rows are matched to toolforme
          clicks by <span className="font-mono">External&nbsp;ID = click rid</span> and the matched clicks
          are marked converted with their payout. Preview first — nothing is
          written until you click Import.
        </p>
      </div>

      <div className="border border-slate-200 rounded-lg p-5 space-y-4">
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">ClickFlare conversions CSV / XLSX</label>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xlsx,.xlsm"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
            className="block w-full text-sm text-slate-700 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-slate-900 file:text-white hover:file:bg-slate-700"
          />
          {file && <p className="text-xs text-slate-500 mt-1">{file.name}</p>}
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={excludeFacebook} onChange={(e) => setExcludeFacebook(e.target.checked)} />
          Exclude Facebook / Meta rows (by <span className="font-mono text-xs">is_facebook</span> or the known FB traffic source)
        </label>

        <div className="flex gap-3 pt-1">
          <Button variant="outline" disabled={busy || !file} onClick={() => run(true)}>
            <UploadSimple size={16} className="mr-1.5" /> Preview (dry run)
          </Button>
          <Button disabled={busy || !file || !result} onClick={() => run(false)}>
            <CheckCircle size={16} className="mr-1.5" /> Import matched conversions
          </Button>
        </div>
        {!result && <p className="text-xs text-slate-400">Run a preview before importing.</p>}
      </div>

      {result && (
        <div className="border border-slate-200 rounded-lg p-5 space-y-4">
          <div className="flex items-center gap-2">
            {wasDryRun
              ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded"><Warning size={14} /> Dry run — nothing written</span>
              : <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded"><CheckCircle size={14} /> Imported — {result.updated} click(s) updated</span>}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Rows parsed" value={result.parsed_rows} />
            <Stat label="Distinct clicks" value={result.distinct_clicks_in_file} />
            <Stat label="Conversions" value={result.conversions_total} />
            <Stat label="Payout (file)" value={`$${result.payout_total}`} />
            <Stat label="Matched clicks" value={result.matched_clicks} tone="text-emerald-600" />
            <Stat label="Unmatched" value={result.unmatched_clicks} tone={result.unmatched_clicks ? "text-red-500" : "text-slate-900"} />
            <Stat label="Skipped: FB" value={result.skipped_facebook} />
            <Stat label="Importable payout" value={`$${result.payout_importable}`} tone="text-emerald-600" />
          </div>

          {(result.skipped_unsubstituted_macro > 0) && (
            <p className="text-xs text-amber-700 bg-amber-50 rounded p-2">
              {result.skipped_unsubstituted_macro} row(s) had a literal <span className="font-mono">{"{rc_click_id}"}</span> External ID —
              those clicks happened before the tracker link was live and can't be matched.
            </p>
          )}
          {(result.wrong_brand_clicks > 0) && (
            <p className="text-xs text-amber-700 bg-amber-50 rounded p-2">
              {result.wrong_brand_clicks} External ID(s) matched a click on a different brand — left untouched.
            </p>
          )}

          {result.by_day && Object.keys(result.by_day).length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-600 mb-1">Conversions by day (file)</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(result.by_day).map(([d, n]) => (
                  <span key={d} className="text-xs font-mono bg-slate-100 text-slate-700 rounded px-2 py-0.5">{d}: {n}</span>
                ))}
              </div>
            </div>
          )}

          {result.sample_unmatched?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-600 mb-1">Sample unmatched External IDs</p>
              <div className="text-[11px] font-mono text-slate-500 space-y-0.5">
                {result.sample_unmatched.map((r) => <div key={r}>{r}</div>)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
